import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  tap,
  finalize,
  filter,
  map,
  of,
  catchError,
  Subject,
  debounceTime,
  switchMap,
  delay,
  expand,
  take,
  EMPTY,
} from 'rxjs';
import {
  LessonInputData,
  LessonObjective,
  LessonBlock,
  LessonSlide,
  LessonPlanStatus,
  STATUS_TO_MAX_STEP,
  DEFAULT_INPUT_DATA,
  DEFAULT_TECH_DETAILS,
  GoogleSlidesData,
  SlideThumbnail,
  SlideThumbnailsResponse,
  GenerationStatus,
  GenerationStatusResponse,
} from './lesson-plan.models';
import {
  LessonPlanApiService,
  LessonPlanResponse,
  UploadProgress,
} from './lesson-plan-api.service';
import { environment } from '../../../../../environments/environment';
import { AmplitudeService } from '../../../../common/analytics/amplitude.service';

@Injectable({
  providedIn: 'root',
})
export class LessonPlanService {
  private readonly apiService = inject(LessonPlanApiService);
  private readonly amplitudeService = inject(AmplitudeService);

  // Debounced structure sync trigger
  private readonly structureSyncTrigger$$ = new Subject<void>();

  // Current lesson plan ID from backend
  private readonly lessonPlanId$$ = new BehaviorSubject<string | null>(null);
  readonly lessonPlanId$ = this.lessonPlanId$$.asObservable();

  // Lesson plan status from backend
  private readonly status$$ = new BehaviorSubject<LessonPlanStatus>(LessonPlanStatus.DRAFT);
  readonly status$ = this.status$$.asObservable();

  // Max step the user can navigate to based on status
  readonly maxAllowedStep$ = this.status$.pipe(
    map((status) => STATUS_TO_MAX_STEP[status] ?? 1)
  );

  // Loading state for fetching lesson plan
  private readonly isLoading$$ = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = this.isLoading$$.asObservable();

  private readonly currentStep$$ = new BehaviorSubject<number>(1);
  readonly currentStep$ = this.currentStep$$.asObservable();

  private readonly inputData$$ = new BehaviorSubject<LessonInputData>(DEFAULT_INPUT_DATA);
  readonly inputData$ = this.inputData$$.asObservable();

  // File upload state
  private readonly uploadedFiles$$ = new BehaviorSubject<File[]>([]);
  readonly uploadedFiles$ = this.uploadedFiles$$.asObservable();

  private readonly isUploading$$ = new BehaviorSubject<boolean>(false);
  readonly isUploading$ = this.isUploading$$.asObservable();

  private readonly uploadProgress$$ = new BehaviorSubject<number>(0);
  readonly uploadProgress$ = this.uploadProgress$$.asObservable();

  // Loading state for AI generation
  private readonly isGenerating$$ = new BehaviorSubject<boolean>(false);
  readonly isGenerating$ = this.isGenerating$$.asObservable();

  private readonly objectives$$ = new BehaviorSubject<LessonObjective[]>([]);
  readonly objectives$ = this.objectives$$.asObservable();

  private readonly selectedObjectives$$ = new BehaviorSubject<LessonObjective[]>([]);
  readonly selectedObjectives$ = this.selectedObjectives$$.asObservable();

  private readonly planStructure$$ = new BehaviorSubject<LessonBlock[]>([]);
  readonly planStructure$ = this.planStructure$$.asObservable();

  private readonly slides$$ = new BehaviorSubject<LessonSlide[]>([]);
  readonly slides$ = this.slides$$.asObservable();

  // Google Slides integration
  private readonly googleSlides$$ = new BehaviorSubject<GoogleSlidesData | null>(null);
  readonly googleSlides$ = this.googleSlides$$.asObservable();

  private readonly thumbnails$$ = new BehaviorSubject<SlideThumbnail[]>([]);
  readonly thumbnails$ = this.thumbnails$$.asObservable();

  private readonly isFetchingThumbnails$$ = new BehaviorSubject<boolean>(false);
  readonly isFetchingThumbnails$ = this.isFetchingThumbnails$$.asObservable();

  // Error state
  private readonly error$$ = new BehaviorSubject<string | null>(null);
  readonly error$ = this.error$$.asObservable();

  constructor() {
    // Set up debounced structure sync (500ms debounce for typing)
    this.structureSyncTrigger$$.pipe(debounceTime(500)).subscribe(() => {
      this.syncStructureToBackend();
    });
  }

  get currentStep(): number {
    return this.currentStep$$.value;
  }

  get inputData(): LessonInputData {
    return this.inputData$$.value;
  }

  get lessonPlanId(): string | null {
    return this.lessonPlanId$$.value;
  }

  get status(): LessonPlanStatus {
    return this.status$$.value;
  }

  get maxAllowedStep(): number {
    return STATUS_TO_MAX_STEP[this.status$$.value] ?? 1;
  }

  get isLoading(): boolean {
    return this.isLoading$$.value;
  }

  get objectives(): LessonObjective[] {
    return this.objectives$$.value;
  }

  get selectedObjectives(): LessonObjective[] {
    return this.selectedObjectives$$.value;
  }

  get planStructure(): LessonBlock[] {
    return this.planStructure$$.value;
  }

  get slides(): LessonSlide[] {
    return this.slides$$.value;
  }

  get googleSlides(): GoogleSlidesData | null {
    return this.googleSlides$$.value;
  }

  get thumbnails(): SlideThumbnail[] {
    return this.thumbnails$$.value;
  }

  get uploadedFiles(): File[] {
    return this.uploadedFiles$$.value;
  }

  private readonly stepNames: Record<number, string> = {
    1: 'basics',
    2: 'settings',
    3: 'topic',
    4: 'objectives',
    5: 'structure',
    6: 'present',
  };

  /**
   * Set the current step, respecting the max allowed step based on status
   */
  setStep(step: number): void {
    const maxStep = this.maxAllowedStep;
    const clampedStep = Math.min(Math.max(1, step), maxStep);
    if (clampedStep !== this.currentStep$$.value) {
      this.currentStep$$.next(clampedStep);
      this.trackStepNavigation(clampedStep);
    }
  }

  nextStep(): void {
    const maxStep = this.maxAllowedStep;
    if (this.currentStep$$.value < maxStep) {
      const newStep = this.currentStep$$.value + 1;
      this.currentStep$$.next(newStep);
      this.trackStepNavigation(newStep);
    }
  }

  prevStep(): void {
    if (this.currentStep$$.value > 1) {
      const newStep = this.currentStep$$.value - 1;
      this.currentStep$$.next(newStep);
      this.trackStepNavigation(newStep);
    }
  }

  private trackStepNavigation(step: number): void {
    const stepName = this.stepNames[step] ?? `step_${step}`;
    this.amplitudeService.trackEvent(`lesson_plan_step_${stepName}_viewed`);
  }

  /**
   * Check if user can navigate to a specific step
   */
  canNavigateToStep(step: number): boolean {
    return step >= 1 && step <= this.maxAllowedStep;
  }

  /**
   * Load an existing lesson plan by ID and populate the service state
   */
  loadLessonPlan(id: string): Observable<LessonPlanResponse | null> {
    this.isLoading$$.next(true);
    this.error$$.next(null);

    return this.apiService.getLessonPlan(id).pipe(
      tap((lessonPlan) => {
        // Populate all state from the loaded lesson plan
        this.lessonPlanId$$.next(lessonPlan.id);
        this.status$$.next(lessonPlan.status);
        this.inputData$$.next({
          topic: lessonPlan.topic,
          grade: lessonPlan.grade,
          tier1SubjectId: lessonPlan.tier1SubjectId || null,
          tier2SubjectId: lessonPlan.tier2SubjectId || null,
          usState: lessonPlan.usState || null,
          duration: lessonPlan.duration,
          instructionalModel: lessonPlan.instructionalModel || null,
          techRatio: lessonPlan.techRatio || null,
          techDetails: lessonPlan.techDetails || { ...DEFAULT_TECH_DETAILS },
        });
        this.objectives$$.next(lessonPlan.objectives || []);
        this.selectedObjectives$$.next(lessonPlan.selectedObjectives || []);
        this.planStructure$$.next(lessonPlan.planStructure || []);
        this.slides$$.next(lessonPlan.slides || []);
        this.googleSlides$$.next(lessonPlan.googleSlides || null);

        // Set current step to max allowed based on status
        const maxStep = STATUS_TO_MAX_STEP[lessonPlan.status] ?? 1;
        this.currentStep$$.next(maxStep);
        this.trackStepNavigation(maxStep);
      }),
      catchError((err) => {
        this.error$$.next('Failed to load lesson plan. It may not exist or you may not have access.');
        return of(null);
      }),
      finalize(() => this.isLoading$$.next(false))
    );
  }

  /**
   * Update status locally (will be synced with backend on relevant operations)
   */
  setStatus(status: LessonPlanStatus): void {
    this.status$$.next(status);
  }

  updateInputData(data: Partial<LessonInputData>): void {
    this.inputData$$.next({ ...this.inputData$$.value, ...data });
  }

  // File upload methods
  addFiles(files: File[]): void {
    const currentFiles = this.uploadedFiles$$.value;
    // Filter out duplicates by name
    const newFiles = files.filter(
      (file) => !currentFiles.some((f) => f.name === file.name && f.size === file.size)
    );
    this.uploadedFiles$$.next([...currentFiles, ...newFiles]);
  }

  removeFile(file: File): void {
    const files = this.uploadedFiles$$.value.filter((f) => f !== file);
    this.uploadedFiles$$.next(files);
  }

  clearFiles(): void {
    this.uploadedFiles$$.next([]);
  }

  /**
   * Create lesson plan only without generating objectives (Step 1 -> Step 2 transition)
   * This is called when user clicks "Continue" button on Basics step
   * Creates a lesson plan with grade, subject, and state only (settings are added in Step 2)
   */
  createLessonPlanOnly(): Observable<void> {
    this.error$$.next(null);

    const inputData = this.inputData$$.value;
    const existingLessonPlanId = this.lessonPlanId$$.value;

    // Only send fields collected in Step 1 (Basics)
    // Settings (duration, instructionalModel, techRatio, techDetails) are set in Step 2
    const requestData = {
      grade: inputData.grade,
      tier1SubjectId: inputData.tier1SubjectId,
      tier2SubjectId: inputData.tier2SubjectId,
      usState: inputData.usState,
    };

    // If lesson plan already exists, just update it; otherwise create new
    const operation$ = existingLessonPlanId
      ? this.apiService.updateLessonPlan(existingLessonPlanId, requestData)
      : this.apiService.createLessonPlan(requestData);

    return operation$.pipe(
      tap((lessonPlan) => {
        this.lessonPlanId$$.next(lessonPlan.id);
        this.status$$.next(lessonPlan.status);
      }),
      map(() => undefined),
      catchError((err) => {
        this.error$$.next('Failed to create lesson plan. Please try again.');
        throw err;
      })
    );
  }

  /**
   * Complete settings step (Step 2 -> Step 3 transition)
   * This is called when user clicks "Continue" on the Settings step
   * Updates settings and sets status to SETTINGS_COMPLETED
   */
  completeSettingsStep(): Observable<void> {
    this.error$$.next(null);

    const inputData = this.inputData$$.value;
    const lessonPlanId = this.lessonPlanId$$.value;

    if (!lessonPlanId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No lesson plan ID'));
      });
    }

    const requestData = {
      grade: inputData.grade,
      tier1SubjectId: inputData.tier1SubjectId,
      tier2SubjectId: inputData.tier2SubjectId,
      usState: inputData.usState,
      duration: inputData.duration,
      instructionalModel: inputData.instructionalModel,
      techRatio: inputData.techRatio,
      techDetails: inputData.techDetails,
    };

    // First update the lesson plan with settings data, then update status
    // Chain the status update properly to ensure it completes before emitting
    return this.apiService.updateLessonPlan(lessonPlanId, requestData).pipe(
      switchMap(() => this.apiService.updateStatus(lessonPlanId, LessonPlanStatus.SETTINGS_COMPLETED)),
      tap((lessonPlan: LessonPlanResponse) => {
        this.status$$.next(lessonPlan.status);
      }),
      map(() => undefined),
      catchError((err) => {
        this.error$$.next('Failed to save settings. Please try again.');
        throw err;
      })
    );
  }

  /**
   * Update topic and generate objectives (Step 3 -> Step 4 transition)
   * This is called when user clicks "Generate Objectives" on the topic step
   */
  updateTopicAndGenerateObjectives(): Observable<LessonObjective[]> {
    this.error$$.next(null);
    this.isGenerating$$.next(true);

    const inputData = this.inputData$$.value;
    const lessonPlanId = this.lessonPlanId$$.value;

    if (!lessonPlanId) {
      this.isGenerating$$.next(false);
      return new Observable((subscriber) => {
        subscriber.error(new Error('No lesson plan ID'));
      });
    }

    const requestData = {
      topic: inputData.topic,
      grade: inputData.grade,
      tier1SubjectId: inputData.tier1SubjectId,
      tier2SubjectId: inputData.tier2SubjectId,
      usState: inputData.usState,
      duration: inputData.duration,
      instructionalModel: inputData.instructionalModel,
      techRatio: inputData.techRatio,
      techDetails: inputData.techDetails,
    };

    return new Observable<LessonObjective[]>((subscriber) => {
      // Step 1: Update lesson plan with topic
      this.apiService.updateLessonPlan(lessonPlanId, requestData).subscribe({
        next: () => {
          // Step 2: Upload files if any
          const files = this.uploadedFiles$$.value;
          if (files.length > 0) {
            this.isUploading$$.next(true);
            this.apiService
              .uploadFiles(lessonPlanId, files)
              .pipe(
                filter((progress) => progress.completed),
                finalize(() => this.isUploading$$.next(false))
              )
              .subscribe({
                next: () => {
                  // Step 3: Generate objectives after upload
                  this.generateObjectivesInternal(lessonPlanId, subscriber);
                },
                error: (err) => {
                  this.error$$.next('Failed to upload files. Please try again.');
                  this.isGenerating$$.next(false);
                  subscriber.error(err);
                },
              });
          } else {
            // Step 3: Generate objectives without files
            this.generateObjectivesInternal(lessonPlanId, subscriber);
          }
        },
        error: (err) => {
          this.error$$.next('Failed to update lesson plan. Please try again.');
          this.isGenerating$$.next(false);
          subscriber.error(err);
        },
      });
    });
  }

  /**
   * Create or update lesson plan and generate objectives
   * This is called when user clicks "Next: Objectives" button
   * If a lesson plan already exists (user went back), it updates the existing one instead of creating new
   * @deprecated Use createLessonPlanOnly() and updateTopicAndGenerateObjectives() instead
   */
  createAndGenerateObjectives(): Observable<LessonObjective[]> {
    this.error$$.next(null);
    this.isGenerating$$.next(true);

    const inputData = this.inputData$$.value;
    const existingLessonPlanId = this.lessonPlanId$$.value;

    const requestData = {
      topic: inputData.topic,
      grade: inputData.grade,
      tier1SubjectId: inputData.tier1SubjectId,
      tier2SubjectId: inputData.tier2SubjectId,
      usState: inputData.usState,
      duration: inputData.duration,
      instructionalModel: inputData.instructionalModel,
      techRatio: inputData.techRatio,
      techDetails: inputData.techDetails,
    };

    return new Observable<LessonObjective[]>((subscriber) => {
      // Step 1: Create new lesson plan OR update existing one
      const lessonPlanOperation$ = existingLessonPlanId
        ? this.apiService.updateLessonPlan(existingLessonPlanId, requestData)
        : this.apiService.createLessonPlan(requestData);

      lessonPlanOperation$.subscribe({
        next: (lessonPlan) => {
          this.lessonPlanId$$.next(lessonPlan.id);

          // Step 2: Upload files if any (only for new lesson plans without existing files)
          const files = this.uploadedFiles$$.value;
          if (files.length > 0 && !existingLessonPlanId) {
            this.isUploading$$.next(true);
            this.apiService
              .uploadFiles(lessonPlan.id, files)
              .pipe(
                filter((progress) => progress.completed),
                finalize(() => this.isUploading$$.next(false))
              )
              .subscribe({
                next: () => {
                  // Step 3: Generate objectives after upload
                  this.generateObjectivesInternal(lessonPlan.id, subscriber);
                },
                error: (err) => {
                  this.error$$.next('Failed to upload files. Please try again.');
                  this.isGenerating$$.next(false);
                  subscriber.error(err);
                },
              });
          } else {
            // Step 3: Generate objectives without files
            this.generateObjectivesInternal(lessonPlan.id, subscriber);
          }
        },
        error: (err) => {
          this.error$$.next(
            existingLessonPlanId
              ? 'Failed to update lesson plan. Please try again.'
              : 'Failed to create lesson plan. Please try again.'
          );
          this.isGenerating$$.next(false);
          subscriber.error(err);
        },
      });
    });
  }

  /** Polling interval in milliseconds */
  private readonly POLLING_INTERVAL_MS = 2000;

  /**
   * Poll for generation status until complete or failed
   * Uses expand() for sequential polling - each poll waits for the previous to complete,
   * then delays before the next request. This is more resilient to browser timer throttling
   * than using interval/timer which can be throttled in background tabs.
   */
  private pollForGenerationStatus(
    lessonPlanId: string,
    expectedCompletedStatus: LessonPlanStatus
  ): Observable<GenerationStatusResponse> {
    let pollCount = 0;

    const makeRequest = () => {
      pollCount++;
      return this.apiService.getGenerationStatus(lessonPlanId);
    };

    // Initial request, then expand with delay for subsequent requests
    return makeRequest().pipe(
      expand((response) => {
        // Stop polling if generation is complete or failed
        if (
          response.generationStatus === GenerationStatus.IDLE ||
          response.generationStatus === GenerationStatus.FAILED
        ) {
          return EMPTY; // Stop expanding
        }
        // Wait for polling interval, then make next request
        return of(null).pipe(
          delay(this.POLLING_INTERVAL_MS),
          switchMap(() => makeRequest())
        );
      }),
      filter(
        (response) =>
          response.generationStatus === GenerationStatus.IDLE ||
          response.generationStatus === GenerationStatus.FAILED
      ),
      take(1)
    );
  }

  private generateObjectivesInternal(
    lessonPlanId: string,
    subscriber: {
      next: (value: LessonObjective[]) => void;
      error: (err: any) => void;
      complete: () => void;
    }
  ): void {
    // Start async generation
    this.apiService.startObjectivesGeneration(lessonPlanId).subscribe({
      next: () => {
        // Poll for status until complete
        this.pollForGenerationStatus(lessonPlanId, LessonPlanStatus.OBJECTIVES_GENERATED)
          .pipe(finalize(() => this.isGenerating$$.next(false)))
          .subscribe({
            next: (statusResponse) => {
              if (statusResponse.generationStatus === GenerationStatus.FAILED) {
                this.error$$.next(
                  statusResponse.generationError || 'Failed to generate objectives. Please try again.'
                );
                subscriber.error(new Error(statusResponse.generationError || 'Generation failed'));
                return;
              }

              // Fetch the updated lesson plan to get the objectives
              this.apiService.getLessonPlan(lessonPlanId).subscribe({
                next: (lessonPlan) => {
                  const objectivesWithNoSelection = lessonPlan.objectives.map((obj) => ({
                    ...obj,
                    selected: false,
                  }));
                  this.objectives$$.next(objectivesWithNoSelection);
                  this.status$$.next(lessonPlan.status);
                  subscriber.next(objectivesWithNoSelection);
                  subscriber.complete();
                },
                error: (err) => {
                  this.error$$.next('Failed to fetch lesson plan. Please try again.');
                  subscriber.error(err);
                },
              });
            },
            error: (err) => {
              this.error$$.next('Failed to check generation status. Please try again.');
              subscriber.error(err);
            },
          });
      },
      error: (err) => {
        this.error$$.next('Failed to start objectives generation. Please try again.');
        this.isGenerating$$.next(false);
        subscriber.error(err);
      },
    });
  }

  setObjectives(objectives: LessonObjective[]): void {
    this.objectives$$.next(objectives);
  }

  toggleObjective(id: string): void {
    const updated = this.objectives$$.value.map((o) =>
      o.id === id ? { ...o, selected: !o.selected } : o
    );
    this.objectives$$.next(updated);
  }

  addCustomObjective(text: string): void {
    const currentGrade = this.inputData$$.value.grade;
    const newObj: LessonObjective = {
      id: `custom-${Date.now()}`,
      standard: 'Custom',
      title: text, // For custom objectives, title is the same as text
      text,
      rationale: 'Teacher added objective',
      alignmentCriteria: [], // No alignment criteria for custom objectives
      selected: true,
      type: 'manual',
      grades: currentGrade !== null ? [currentGrade] : [],
      relevance: 10, // Custom objectives are fully relevant by definition
      createdAt: Date.now(),
    };
    const updatedObjectives = [...this.objectives$$.value, newObj];
    this.objectives$$.next(updatedObjectives);

    // Sync with backend immediately
    const lessonPlanId = this.lessonPlanId$$.value;
    if (lessonPlanId) {
      this.apiService.updateObjectives(lessonPlanId, updatedObjectives).subscribe({
        error: (err) => {
          console.error('Failed to save custom objective to backend:', err);
          this.error$$.next('Failed to save objective. Please try again.');
        },
      });
    }
  }

  deleteObjective(id: string): void {
    const updatedObjectives = this.objectives$$.value.filter((o) => o.id !== id);
    this.objectives$$.next(updatedObjectives);

    // Sync with backend
    const lessonPlanId = this.lessonPlanId$$.value;
    if (lessonPlanId) {
      this.apiService.updateObjectives(lessonPlanId, updatedObjectives).subscribe({
        error: (err) => {
          console.error('Failed to delete objective from backend:', err);
          this.error$$.next('Failed to delete objective. Please try again.');
        },
      });
    }
  }

  confirmObjectives(): void {
    const selected = this.objectives$$.value.filter((o) => o.selected);
    this.selectedObjectives$$.next(selected);

    // Sync with backend
    const lessonPlanId = this.lessonPlanId$$.value;
    if (lessonPlanId) {
      this.apiService.updateObjectives(lessonPlanId, this.objectives$$.value).subscribe();
    }
  }

  /**
   * Confirm objectives and generate lesson plan structure (Step 4 -> Step 5 transition)
   * This is called when user clicks "Confirm" button on objectives step
   * Uses async generation with polling
   */
  confirmObjectivesAndGenerateStructure(): Observable<LessonBlock[]> {
    const selected = this.objectives$$.value.filter((o) => o.selected);
    this.selectedObjectives$$.next(selected);
    this.error$$.next(null);
    this.isGenerating$$.next(true);

    const lessonPlanId = this.lessonPlanId$$.value;
    if (!lessonPlanId) {
      this.isGenerating$$.next(false);
      return new Observable((subscriber) => {
        subscriber.error(new Error('No lesson plan ID'));
      });
    }

    const selectedObjectiveIds = selected.map((o) => o.id);

    return new Observable<LessonBlock[]>((subscriber) => {
      // Start async generation
      this.apiService.startStructureGeneration(lessonPlanId, selectedObjectiveIds).subscribe({
        next: () => {
          // Poll for status until complete
          this.pollForGenerationStatus(lessonPlanId, LessonPlanStatus.STRUCTURE_GENERATED)
            .pipe(finalize(() => this.isGenerating$$.next(false)))
            .subscribe({
              next: (statusResponse) => {
                if (statusResponse.generationStatus === GenerationStatus.FAILED) {
                  this.error$$.next(
                    statusResponse.generationError || 'Failed to generate structure. Please try again.'
                  );
                  subscriber.error(new Error(statusResponse.generationError || 'Generation failed'));
                  return;
                }

                // Fetch the updated lesson plan to get the structure
                this.apiService.getLessonPlan(lessonPlanId).subscribe({
                  next: (lessonPlan) => {
                    this.planStructure$$.next(lessonPlan.planStructure);
                    this.status$$.next(lessonPlan.status);
                    subscriber.next(lessonPlan.planStructure);
                    subscriber.complete();
                  },
                  error: (err) => {
                    this.error$$.next('Failed to fetch lesson plan. Please try again.');
                    subscriber.error(err);
                  },
                });
              },
              error: (err) => {
                this.error$$.next('Failed to check generation status. Please try again.');
                subscriber.error(err);
              },
            });
        },
        error: (err) => {
          this.error$$.next('Failed to start structure generation. Please try again.');
          this.isGenerating$$.next(false);
          subscriber.error(err);
        },
      });
    });
  }

  setPlanStructure(plan: LessonBlock[]): void {
    this.planStructure$$.next(plan);
  }

  /**
   * Confirm structure and generate presentation slides (Step 5 -> Step 6 transition)
   * This is called when user clicks "Create Slides" button on structure step
   * Structure changes are already synced on change, so we just generate slides
   * Uses async generation with polling
   */
  confirmStructureAndGenerateSlides(): Observable<LessonSlide[]> {
    this.error$$.next(null);
    this.isGenerating$$.next(true);

    const lessonPlanId = this.lessonPlanId$$.value;
    if (!lessonPlanId) {
      this.isGenerating$$.next(false);
      return new Observable((subscriber) => {
        subscriber.error(new Error('No lesson plan ID'));
      });
    }

    return new Observable<LessonSlide[]>((subscriber) => {
      // Start async generation
      this.apiService.startSlidesGeneration(lessonPlanId).subscribe({
        next: () => {
          // Poll for status until complete
          this.pollForGenerationStatus(lessonPlanId, LessonPlanStatus.CONTENT_GENERATED)
            .pipe(finalize(() => this.isGenerating$$.next(false)))
            .subscribe({
              next: (statusResponse) => {
                if (statusResponse.generationStatus === GenerationStatus.FAILED) {
                  this.error$$.next(
                    statusResponse.generationError || 'Failed to generate slides. Please try again.'
                  );
                  subscriber.error(new Error(statusResponse.generationError || 'Generation failed'));
                  return;
                }

                // Fetch the updated lesson plan to get the slides
                this.apiService.getLessonPlan(lessonPlanId).subscribe({
                  next: (lessonPlan) => {
                    this.slides$$.next(lessonPlan.slides);
                    this.googleSlides$$.next(lessonPlan.googleSlides);
                    this.status$$.next(lessonPlan.status);
                    // Clear old thumbnails - they'll be fetched fresh when viewing
                    this.thumbnails$$.next([]);
                    subscriber.next(lessonPlan.slides);
                    subscriber.complete();
                  },
                  error: (err) => {
                    this.error$$.next('Failed to fetch lesson plan. Please try again.');
                    subscriber.error(err);
                  },
                });
              },
              error: (err) => {
                this.error$$.next('Failed to check generation status. Please try again.');
                subscriber.error(err);
              },
            });
        },
        error: (err) => {
          this.error$$.next('Failed to start slides generation. Please try again.');
          this.isGenerating$$.next(false);
          subscriber.error(err);
        },
      });
    });
  }

  /**
   * Fetch fresh thumbnails for the current lesson plan's Google Slides presentation
   */
  fetchThumbnails(): Observable<SlideThumbnail[]> {
    const lessonPlanId = this.lessonPlanId$$.value;
    if (!lessonPlanId) {
      return of([]);
    }

    const googleSlides = this.googleSlides$$.value;
    if (!googleSlides?.presentationId) {
      return of([]);
    }

    this.isFetchingThumbnails$$.next(true);

    return this.apiService.getSlideThumbnails(lessonPlanId).pipe(
      map((response) => {
        // Transform thumbnail paths to full URLs if needed
        // Backend now returns absolute S3 URLs (https://...)
        // For backward compatibility, also handle relative paths
        const apiUrl = environment.apiUrl;
        return response.thumbnails.map((thumbnail) => ({
          ...thumbnail,
          thumbnailUrl: thumbnail.thumbnailUrl
            ? thumbnail.thumbnailUrl.startsWith('http')
              ? thumbnail.thumbnailUrl
              : `${apiUrl}${thumbnail.thumbnailUrl}`
            : '',
        }));
      }),
      tap((thumbnails) => {
        this.thumbnails$$.next(thumbnails);
      }),
      catchError((err) => {
        console.error('Failed to fetch thumbnails:', err);
        return of([]);
      }),
      finalize(() => this.isFetchingThumbnails$$.next(false))
    );
  }

  updateBlock(id: number, field: keyof LessonBlock, value: string | number): void {
    const updated = this.planStructure$$.value.map((block) =>
      block.id === id ? { ...block, [field]: value } : block
    );
    this.planStructure$$.next(updated);
    // Trigger debounced sync for edits (user might be typing)
    this.structureSyncTrigger$$.next();
  }

  deleteBlock(id: number): void {
    const updated = this.planStructure$$.value.filter((block) => block.id !== id);
    this.planStructure$$.next(updated);
    // Sync immediately for delete
    this.syncStructureToBackend();
  }

  addBlock(): void {
    const blocks = this.planStructure$$.value;
    const maxId = blocks.length > 0 ? Math.max(...blocks.map((b) => b.id)) : 0;
    const newBlock: LessonBlock = {
      id: maxId + 1,
      time: '',
      duration: 5,
      phase: 'New Phase',
      activity: 'New Activity',
      description: 'Add activity details here...',
    };
    this.planStructure$$.next([...blocks, newBlock]);
    // Sync immediately for add
    this.syncStructureToBackend();
  }

  moveBlockUp(id: number): void {
    const blocks = [...this.planStructure$$.value];
    const index = blocks.findIndex((b) => b.id === id);
    if (index > 0) {
      [blocks[index - 1], blocks[index]] = [blocks[index], blocks[index - 1]];
      this.planStructure$$.next(blocks);
      this.syncStructureToBackend();
    }
  }

  moveBlockDown(id: number): void {
    const blocks = [...this.planStructure$$.value];
    const index = blocks.findIndex((b) => b.id === id);
    if (index < blocks.length - 1) {
      [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
      this.planStructure$$.next(blocks);
      this.syncStructureToBackend();
    }
  }

  private syncStructureToBackend(): void {
    const lessonPlanId = this.lessonPlanId$$.value;
    if (!lessonPlanId) {
      return;
    }

    this.apiService.updateStructure(lessonPlanId, this.planStructure$$.value).subscribe({
      error: (err) => {
        console.error('Failed to sync structure to backend:', err);
        this.error$$.next('Failed to save changes. Please try again.');
      },
    });
  }

  setSlides(slides: LessonSlide[]): void {
    this.slides$$.next(slides);
  }

  /**
   * Reset all state to initial values.
   * Note: Does not track step navigation - caller should handle tracking if needed.
   */
  reset(): void {
    this.currentStep$$.next(1);
    this.lessonPlanId$$.next(null);
    this.status$$.next(LessonPlanStatus.DRAFT);
    this.inputData$$.next(DEFAULT_INPUT_DATA);
    this.uploadedFiles$$.next([]);
    this.uploadProgress$$.next(0);
    this.isUploading$$.next(false);
    this.isLoading$$.next(false);
    this.isGenerating$$.next(false);
    this.objectives$$.next([]);
    this.selectedObjectives$$.next([]);
    this.planStructure$$.next([]);
    this.slides$$.next([]);
    this.googleSlides$$.next(null);
    this.thumbnails$$.next([]);
    this.isFetchingThumbnails$$.next(false);
    this.error$$.next(null);
  }

  /**
   * Assign the current lesson plan to the authenticated user.
   * Used after signup/login in the marketing funnel.
   */
  assignToCurrentUser(): Observable<void> {
    const lessonPlanId = this.lessonPlanId$$.value;
    if (!lessonPlanId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No lesson plan ID'));
      });
    }

    return this.apiService.assignToUser(lessonPlanId).pipe(
      tap((lessonPlan) => {
        // Update local state with the assigned lesson plan
        this.status$$.next(lessonPlan.status);
      }),
      map(() => undefined),
      catchError((err) => {
        // If the lesson plan already belongs to the user, that's fine
        if (err.status === 400 && err.error?.message?.includes('already belongs')) {
          return of(undefined);
        }
        console.error('Failed to assign lesson plan to user:', err);
        throw err;
      })
    );
  }

  /**
   * Export lesson plan as Teacher Guide PDF
   * Downloads the PDF file directly to the user's device
   */
  exportToPdf(): Observable<void> {
    const lessonPlanId = this.lessonPlanId$$.value;
    if (!lessonPlanId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No lesson plan ID'));
      });
    }

    return this.apiService.exportToPdf(lessonPlanId).pipe(
      tap((blob) => {
        // Create download link
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.inputData.topic.replace(/[^a-zA-Z0-9]/g, '_')}_Teacher_Guide.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }),
      map(() => undefined),
      catchError((err) => {
        console.error('Failed to export PDF:', err);
        this.error$$.next('Failed to export PDF. Please try again.');
        throw err;
      })
    );
  }

  /**
   * Export lesson plan slides as PPTX (PowerPoint format)
   * Downloads the PPTX file directly to the user's device
   * This exports the Google Slides presentation to PPTX, preserving all styles
   */
  exportToPptx(): Observable<void> {
    const lessonPlanId = this.lessonPlanId$$.value;
    if (!lessonPlanId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No lesson plan ID'));
      });
    }

    return this.apiService.exportToPptx(lessonPlanId).pipe(
      tap((blob) => {
        // Create download link
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.inputData.topic.replace(/[^a-zA-Z0-9]/g, '_')}_Slides.pptx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }),
      map(() => undefined),
      catchError((err) => {
        console.error('Failed to export PPTX:', err);
        this.error$$.next('Failed to export PPTX. Please try again.');
        throw err;
      })
    );
  }

  /**
   * Export lesson plan as Administrative Plan DOCX
   * Downloads the DOCX file directly to the user's device
   * Formatted for school administrative review and approval
   */
  exportToDocx(): Observable<void> {
    const lessonPlanId = this.lessonPlanId$$.value;
    if (!lessonPlanId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No lesson plan ID'));
      });
    }

    return this.apiService.exportToDocx(lessonPlanId).pipe(
      tap((blob) => {
        // Create download link
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.inputData.topic.replace(/[^a-zA-Z0-9]/g, '_')}_Admin_Plan.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }),
      map(() => undefined),
      catchError((err) => {
        console.error('Failed to export DOCX:', err);
        this.error$$.next('Failed to export administrative plan. Please try again.');
        throw err;
      })
    );
  }

  /**
   * Export lesson plan as Administrative Plan Google Doc
   * Creates a Google Doc and opens it in a new tab
   */
  exportToGoogleDocs(): Observable<string> {
    const lessonPlanId = this.lessonPlanId$$.value;
    if (!lessonPlanId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No lesson plan ID'));
      });
    }

    return this.apiService.exportToGoogleDocs(lessonPlanId).pipe(
      tap((response) => {
        // Open the Google Doc in a new tab
        window.open(response.documentUrl, '_blank');
      }),
      map((response) => response.documentUrl),
      catchError((err) => {
        console.error('Failed to export to Google Docs:', err);
        this.error$$.next('Failed to export to Google Docs. Please try again.');
        throw err;
      })
    );
  }

  /**
   * Track Google Slides export (when user opens the presentation)
   * Used to increment the export counter
   */
  trackGoogleSlidesExport(): Observable<void> {
    const lessonPlanId = this.lessonPlanId$$.value;
    if (!lessonPlanId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No lesson plan ID'));
      });
    }

    return this.apiService.trackGoogleSlidesExport(lessonPlanId).pipe(
      catchError((err) => {
        // Don't show error to user - tracking is not critical
        console.error('Failed to track Google Slides export:', err);
        throw err;
      })
    );
  }

  generateMarkdown(): string {
    const topic = this.inputData.topic;
    const plan = this.planStructure;
    const slides = this.slides;

    const header = `# Lesson Plan: ${topic}\n\n`;

    const planSection =
      `## 1. Administrative Plan\n` +
      plan
        .map(
          (b) =>
            `### ${b.phase} (${b.time}m)\n- **Activity:** ${b.activity}\n- **Details:** ${b.description}`
        )
        .join('\n\n') +
      '\n\n';

    const slidesSection =
      `## 2. Presentation Slides\n` +
      slides
        .map((s, i) => {
          switch (s.type) {
            case 'greeting': {
              const content = s.content as { title: string; subject: string; grade: string };
              return `### Slide ${i + 1}: Opening\n- **Title:** ${content.title}\n- **Subject:** ${content.subject}\n- **Grade:** ${content.grade}`;
            }
            case 'text': {
              const content = s.content as { title: string; subtitle: string };
              return `### Slide ${i + 1}: ${content.title}\n${content.subtitle}`;
            }
            case 'list': {
              const content = s.content as { title: string; subtitle: string; items: string };
              const items = content.items.split('\n').map(item => `  - ${item}`).join('\n');
              return `### Slide ${i + 1}: ${content.title}\n${content.subtitle}\n${items}`;
            }
            case 'bye': {
              const content = s.content as { text: string };
              return `### Slide ${i + 1}: Closing\n${content.text}`;
            }
            default:
              return `### Slide ${i + 1}\n(Unknown slide type)`;
          }
        })
        .join('\n\n---\n\n');

    return header + planSection + slidesSection;
  }
}
